// ======================================
// Admin CRM Module
// ======================================

import { apiRequest } from '../../lib/api.js';
import { renderAdminLayout, initAdminLayout } from './admin-layout.js';

export function renderCRMAdmin() {
  const content = `
      <div class="admin-page-header">
        <div>
          <h2 class="admin-page-title" style="margin:0;">CRM & Follow-ups</h2>
          <p class="admin-page-subtitle">Track follow-ups, referrals, and customer interactions</p>
        </div>
      </div>

      <!-- Tabs -->
      <div style="display:flex;gap:var(--space-2);margin-bottom:var(--space-6);border-bottom:1px solid var(--color-border);padding-bottom:var(--space-2);">
        <button class="btn btn-primary crm-tab active" data-tab="followups">Follow-ups</button>
        <button class="btn btn-outline crm-tab" data-tab="referrals">Referrals</button>
      </div>

      <!-- Follow-ups Tab -->
      <div id="followups-tab" class="tab-content">
        <div class="admin-card" style="padding:var(--space-6);">
          <h3 style="margin:0 0 var(--space-4);display:flex;align-items:center;gap:var(--space-2);">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></svg>
            Upcoming Follow-ups
          </h3>
          <div id="followups-list">
            <p style="text-align:center;padding:var(--space-6);color:var(--color-text-muted);">Loading...</p>
          </div>
        </div>
      </div>

      <!-- Referrals Tab -->
      <div id="referrals-tab" class="tab-content" style="display:none;">
        <div class="admin-card">
          <div style="overflow-x:auto;">
            <table class="admin-table">
              <thead>
                <tr>
                  <th>Referrer</th>
                  <th>Friend</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Reward</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody id="referrals-tbody">
                <tr><td colspan="6" style="text-align:center;padding:var(--space-6);">Loading...</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;

  return renderAdminLayout('crm', 'CRM & Follow-ups', content);
}

export function initCRMAdmin() {
  initAdminLayout();

  async function loadFollowups() {
    try {
      const data = await apiRequest('/leads?status=contacted&limit=50');
      const leads = data.data?.leads || data.leads || data.data || [];
      const container = document.getElementById('followups-list');

      const upcoming = (Array.isArray(leads) ? leads : []).filter(l => l.follow_up_date).sort((a, b) => new Date(a.follow_up_date) - new Date(b.follow_up_date));

      if (!upcoming.length) {
        container.innerHTML = '<p style="text-align:center;padding:var(--space-6);color:var(--color-text-muted);">No follow-ups scheduled. Assign follow-up dates from Lead Management.</p>';
        return;
      }

      const today = new Date().toISOString().split('T')[0];
      container.innerHTML = upcoming.map(lead => {
        const isOverdue = lead.follow_up_date < today;
        const isToday = lead.follow_up_date === today;
        return `
          <div style="display:flex;align-items:center;gap:var(--space-4);padding:var(--space-4);border-left:3px solid ${isOverdue ? 'var(--color-error)' : isToday ? 'var(--color-warning)' : 'var(--color-success)'};background:${isOverdue ? 'rgba(244,67,54,0.05)' : 'transparent'};border-radius:0 var(--radius-md) var(--radius-md) 0;margin-bottom:var(--space-2);">
            <div style="flex:1;">
              <p style="margin:0;font-weight:600;">${lead.name}</p>
              <p style="margin:2px 0 0;font-size:12px;color:var(--color-text-muted);">${lead.phone} · ${lead.email || 'No email'}</p>
            </div>
            <div style="text-align:right;min-width:100px;">
              <p style="margin:0;font-size:13px;font-weight:600;color:${isOverdue ? 'var(--color-error)' : isToday ? 'var(--color-warning)' : 'var(--color-text)'};">
                ${isOverdue ? '⚠ Overdue' : isToday ? '📞 Today' : new Date(lead.follow_up_date).toLocaleDateString()}
              </p>
              <span class="badge badge-info" style="font-size:10px;margin-top:4px;">${(lead.status || '').replace(/_/g, ' ')}</span>
            </div>
          </div>
        `;
      }).join('');
    } catch (e) {
      document.getElementById('followups-list').innerHTML = '<p style="color:var(--color-error);">Failed to load follow-ups</p>';
    }
  }

  async function loadReferrals() {
    try {
      const data = await apiRequest('/referrals');
      const referrals = data.data || [];
      const tbody = document.getElementById('referrals-tbody');

      if (!referrals.length) {
        tbody.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:var(--space-6);">No referrals yet</td></tr>';
        return;
      }

      tbody.innerHTML = referrals.map(r => `
        <tr>
          <td><strong>${r.referrer_name}</strong></td>
          <td><strong>${r.referee_name || r.friend_name || '—'}</strong></td>
          <td style="font-size:13px;">${new Date(r.created_at).toLocaleDateString()}</td>
          <td><span class="badge ${r.status === 'converted' ? 'badge-success' : r.status === 'pending' ? 'badge-warning' : 'badge-secondary'}">${r.status}</span></td>
          <td style="font-weight:600;">₹${(r.reward_amount || 0).toLocaleString('en-IN')}</td>
          <td>
            <select class="form-input referral-status" data-id="${r.id}" style="font-size:12px;padding:4px 8px;">
              <option value="pending" ${r.status === 'pending' ? 'selected' : ''}>Pending</option>
              <option value="contacted" ${r.status === 'contacted' ? 'selected' : ''}>Contacted</option>
              <option value="converted" ${r.status === 'converted' ? 'selected' : ''}>Converted</option>
              <option value="expired" ${r.status === 'expired' ? 'selected' : ''}>Expired</option>
            </select>
          </td>
        </tr>
      `).join('');

    } catch (e) {
      document.getElementById('referrals-tbody').innerHTML = '<tr><td colspan="6" style="color:var(--color-error);">Failed to load referrals</td></tr>';
    }
  }

  // Tab switching
  document.querySelectorAll('.crm-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.crm-tab').forEach(t => { t.classList.remove('active', 'btn-primary'); t.classList.add('btn-outline'); });
      tab.classList.add('active', 'btn-primary');
      tab.classList.remove('btn-outline');
      const tabName = tab.dataset.tab;
      document.getElementById('followups-tab').style.display = tabName === 'followups' ? 'block' : 'none';
      document.getElementById('referrals-tab').style.display = tabName === 'referrals' ? 'block' : 'none';
      if (tabName === 'referrals') loadReferrals();
    });
  });

  loadFollowups();
}
